/**
 * Delegation-parent row plus its child subtree in the list view.
 *
 * Renders:
 *   - The parent as a `ListViewRow` with an expand/collapse toggle.
 *   - When expanded: each child as a `ListViewRow` with 24px indentation, OR a single self-parallel summary row (see below).
 *   - When collapsed: just the parent row.
 *
 * Self-parallel collapse (default):
 * A self-parallel delegation fans out N children to the same agent. By
 * default — even when the parent is expanded — we render the children
 * as a single summary row (`└─ 5 self-parallel subtasks · Coder`) to
 * avoid visual repetition. Clicking the summary expands it to the full
 * child list.
 *
 * Spec: docs/specs/2026-04-16-session-state-audit.md §3.5.
 */

import { useMemo, useState } from "react";
import type { Task } from "@/api/tasks";
import { ListViewRow } from "./list-view-row";
import { ListViewTreeToggle } from "./list-view-tree-toggle";
import { ListViewSelfParallelSummary } from "./list-view-self-parallel-summary";

interface ListViewTreeNodeProps {
  parent: Task;
  children: Task[];
  onSelectTask: (taskId: string) => void;
  /** Depth of the parent row (for nested trees). Defaults to 0. */
  indent?: number;
  childrenByParent?: Map<string, Task[]>;
}

/**
 * Decide whether this delegation is self-parallel.
 *
 * Prefers the authoritative `parent.delegation_kind` field set by the
 * delegation service (Sprint 2). Falls back to client-side same-agent
 * detection when the field is empty — that keeps legacy tasks (created
 * before Sprint 2 shipped delegation_kind) rendering correctly.
 */
function isSelfParallel(
  parent: Task,
  children: Task[],
): {
  selfParallel: boolean;
  agentName: string | null;
  agentColor: string | null;
} {
  if (children.length < 2) {
    return { selfParallel: false, agentName: null, agentColor: null };
  }

  // Read the shared agent off the children regardless of which branch
  // we take — the summary row needs name + color either way.
  const first = children[0]!.assigned_agent_detail;
  const agentName = first?.name ?? null;
  const agentColor = first?.color ?? null;

  // Authoritative path: trust the server.
  if (parent.delegation_kind === "self_parallel") {
    return { selfParallel: true, agentName, agentColor };
  }
  if (parent.delegation_kind === "cross_agent") {
    return { selfParallel: false, agentName: null, agentColor: null };
  }

  // Fallback for tasks without delegation_kind set (pre-Sprint-2 data).
  if (!first) return { selfParallel: false, agentName: null, agentColor: null };
  const allSame = children.every(
    (c) => c.assigned_agent_detail?.id === first.id,
  );
  if (!allSame) {
    return { selfParallel: false, agentName: null, agentColor: null };
  }
  return { selfParallel: true, agentName, agentColor };
}

export function ListViewTreeNode({
  parent,
  children,
  onSelectTask,
  indent = 0,
  childrenByParent,
}: ListViewTreeNodeProps) {
  const [expanded, setExpanded] = useState(false);
  const [selfParallelExpanded, setSelfParallelExpanded] = useState(false);

  const { selfParallel, agentName, agentColor } = useMemo(
    () => isSelfParallel(parent, children),
    [parent, children],
  );

  const childIndent = indent + 1;

  return (
    <>
      <ListViewRow
        task={parent}
        onSelect={onSelectTask}
        indent={indent}
        toggle={
          <ListViewTreeToggle
            expanded={expanded}
            onToggle={() => setExpanded((v) => !v)}
            childCount={children.length}
          />
        }
      />

      {expanded && selfParallel && !selfParallelExpanded && (
        <ListViewSelfParallelSummary
          count={children.length}
          agentName={agentName}
          agentColor={agentColor}
          indent={childIndent}
          onExpand={() => setSelfParallelExpanded(true)}
        />
      )}

      {expanded &&
        (!selfParallel || selfParallelExpanded) &&
        children.map((child) => {
          const descendants = childrenByParent?.get(child.id);
          return descendants?.length ? (
            <ListViewTreeNode
              key={child.id}
              parent={child}
              children={descendants}
              childrenByParent={childrenByParent}
              onSelectTask={onSelectTask}
              indent={childIndent}
            />
          ) : (
            <ListViewRow
              key={child.id}
              task={child}
              onSelect={onSelectTask}
              indent={childIndent}
            />
          );
        })}
    </>
  );
}
